import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './Configurator.css';

const Configurator = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Configuration state
  const [config, setConfig] = useState({
    case: 'gold',
    dial: 'black',
    strap: 'leather'
  });

  // Base price
  const basePrice = 24500;

  // Price modifiers
  const priceModifiers = {
    case: {
      gold: 0,
      silver: -3000,
      black: -2000
    },
    dial: {
      white: 0,
      black: 0,
      blue: 500
    },
    strap: {
      leather: 0,
      metal: 2000,
      rubber: -500
    }
  };

  // Configuration options
  const options = {
    case: [
      { id: 'gold', name: '18K Rose Gold', color: '#D4AF37' },
      { id: 'silver', name: 'Stainless Steel', color: '#C0C0C0' },
      { id: 'black', name: 'Black Ceramic', color: '#2A2A2A' }
    ],
    dial: [
      { id: 'white', name: 'White Ivory', color: '#F5F5F5' },
      { id: 'black', name: 'Deep Black', color: '#0A0A0A' },
      { id: 'blue', name: 'Midnight Blue', color: '#1E3A8A' }
    ],
    strap: [
      { id: 'leather', name: 'Alligator Leather', icon: '▭' },
      { id: 'metal', name: 'Metal Bracelet', icon: '▬' },
      { id: 'rubber', name: 'Sport Rubber', icon: '▬' }
    ]
  };

  // Calculate total price
  const calculatePrice = () => {
    let total = basePrice;
    total += priceModifiers.case[config.case];
    total += priceModifiers.dial[config.dial];
    total += priceModifiers.strap[config.strap];
    return total.toLocaleString();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleOptionSelect = (category, optionId) => {
    soundManager.play('ui-click', 0.3);
    setConfig(prev => ({
      ...prev,
      [category]: optionId
    }));
  };

  const handleReserve = () => {
    soundManager.play('ui-click', 0.5);
    // In real app, this would open reservation form with selected config
    document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      id="configurator" 
      ref={sectionRef}
      className={`configurator-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="configurator-container">
        {/* Section Header */}
        <div className="configurator-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Bespoke Creation</span>
            <span className="label-line"></span>
          </div>
          <h2 className="configurator-title">Configure Your Timepiece</h2>
          <p className="configurator-subtitle">
            Create your perfect watch. Every configuration is handcrafted 
            to order by our Swiss master watchmakers.
          </p>
        </div>

        {/* Main Configurator */}
        <div className="configurator-main">
          {/* Left Side - Watch Preview */}
          <div className="preview-section">
            <div className="preview-container">
              {/* Watch visualization */}
              <div className="watch-preview">
                {/* Watch case */}
                <div 
                  className="preview-case"
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${options.case.find(c => c.id === config.case)?.color}, ${options.case.find(c => c.id === config.case)?.color}DD)`
                  }}
                >
                  {/* Case bezel */}
                  <div className="preview-bezel"></div>

                  {/* Watch dial */}
                  <div 
                    className="preview-dial"
                    style={{
                      background: `radial-gradient(circle at 40% 40%, ${options.dial.find(d => d.id === config.dial)?.color}, ${options.dial.find(d => d.id === config.dial)?.color}DD)`
                    }}
                  >
                    {/* Hour markers */}
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className="dial-marker"
                        style={{
                          transform: `rotate(${i * 30}deg) translateY(-55px)`
                        }}
                      ></div>
                    ))}

                    {/* Center logo */}
                    <div className="dial-logo">C</div>

                    {/* Hands */}
                    <div className="dial-hands">
                      <div className="hand hour-hand"></div>
                      <div className="hand minute-hand"></div>
                    </div>
                    <div className="hand-pivot"></div>
                  </div>
                </div>

                {/* Strap */}
                <div className="preview-strap">
                  <div className={`strap-visual ${config.strap}`}></div>
                </div>

                {/* Crown */}
                <div 
                  className="preview-crown"
                  style={{
                    background: options.case.find(c => c.id === config.case)?.color
                  }}
                ></div>
              </div>

              {/* Configuration summary */}
              <div className="preview-summary">
                <div className="summary-item">
                  <span className="summary-label">Case:</span>
                  <span className="summary-value">
                    {options.case.find(c => c.id === config.case)?.name}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Dial:</span>
                  <span className="summary-value">
                    {options.dial.find(d => d.id === config.dial)?.name}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Strap:</span>
                  <span className="summary-value">
                    {options.strap.find(s => s.id === config.strap)?.name}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Options */}
          <div className="options-section">
            {/* Case Selection */}
            <div className="option-group">
              <h3 className="option-title">
                <span className="option-icon">◆</span>
                Case Material
              </h3>
              <div className="option-buttons">
                {options.case.map(option => (
                  <button
                    key={option.id}
                    className={`option-btn ${config.case === option.id ? 'active' : ''}`}
                    onClick={() => handleOptionSelect('case', option.id)}
                  >
                    <div 
                      className="option-color"
                      style={{ background: option.color }}
                    ></div>
                    <span className="option-name">{option.name}</span>
                    {config.case === option.id && (
                      <span className="option-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Dial Selection */}
            <div className="option-group">
              <h3 className="option-title">
                <span className="option-icon">◆</span>
                Dial Color
              </h3>
              <div className="option-buttons">
                {options.dial.map(option => (
                  <button
                    key={option.id}
                    className={`option-btn ${config.dial === option.id ? 'active' : ''}`}
                    onClick={() => handleOptionSelect('dial', option.id)}
                  >
                    <div 
                      className="option-color"
                      style={{ background: option.color }}
                    ></div>
                    <span className="option-name">{option.name}</span>
                    {config.dial === option.id && (
                      <span className="option-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Strap Selection */}
            <div className="option-group">
              <h3 className="option-title">
                <span className="option-icon">◆</span>
                Strap Type
              </h3>
              <div className="option-buttons">
                {options.strap.map(option => (
                  <button
                    key={option.id}
                    className={`option-btn ${config.strap === option.id ? 'active' : ''}`}
                    onClick={() => handleOptionSelect('strap', option.id)}
                  >
                    <span className="option-icon-large">{option.icon}</span>
                    <span className="option-name">{option.name}</span>
                    {config.strap === option.id && (
                      <span className="option-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Price and CTA */}
            <div className="configurator-footer">
              <div className="price-container">
                <span className="price-label">Your Configuration</span>
                <span className="price-value">${calculatePrice()}</span>
                <span className="price-note">Handcrafted to order</span>
              </div>

              <button 
                className="reserve-btn"
                onClick={handleReserve}
              >
                Reserve This Configuration
              </button>

              <p className="footer-note">
                Production time: 12-16 weeks. Each timepiece is individually 
                crafted and tested to CHRONOS standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Configurator;